import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { ROUTES_NAMES } from '../components/Layout';
import {
  apiService,
  type CommitPgmFixtureFrameResult,
  type PgmAuditFrame,
  type PgmCueKind,
  type PgmFixturePool,
} from '../services/api';
import {
  auditFrameSrc,
  buildPgmSourceKey,
  CUE_CHIP_LABEL,
  cuesAvailableForRun,
  FRAME_THUMB_HEIGHT,
  FRAME_CARD_GAP,
  FRAME_CARD_MIN_WIDTH,
  framePassesForCue,
  frameRejectReasonForCue,
  frameMatchesCueVerdictFilter,
  INCORRECT_EXAMPLE_REASON,
  incorrectExampleTargetPool,
  isIncorrectExampleSelection,
  normalizeTsec,
  PGM_CUE_KINDS,
  PGM_CUE_VERDICT_FILTER_LABEL,
  PGM_FRAME_SELECTION_COLOR,
  pgmAuditFrameRowHeight,
  type PgmCueVerdictFilter,
  type SavedSelection,
} from './pgmAuditUtils';

const PgmAuditFrameCard = memo(function PgmAuditFrameCard({
  frame,
  contentId,
  runCue,
  availableCues,
  committed,
  selected,
  inExamples,
  incorrectExampleKeys,
  removingKey,
  onToggleFrame,
  onRemoveIncorrectExample,
}: {
  frame: PgmAuditFrame;
  contentId: string;
  runCue: PgmCueKind | 'all';
  availableCues: PgmCueKind[];
  committed: boolean;
  selected: boolean;
  inExamples: boolean;
  incorrectExampleKeys: Set<string>;
  removingKey: string | null;
  onToggleFrame: (tsec: number) => void;
  onRemoveIncorrectExample: (tsec: number, cue: PgmCueKind, pool: PgmFixturePool) => void;
}) {
  const imageSrc = auditFrameSrc(frame.imageUrl);
  const normalizedTsec = normalizeTsec(frame.tsec);

  return (
    <Box
      onClick={() => !committed && onToggleFrame(frame.tsec)}
      sx={{
        border: 2,
        borderColor: committed
          ? 'success.main'
          : inExamples
            ? 'error.dark'
            : selected
              ? PGM_FRAME_SELECTION_COLOR
              : 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: (theme) => {
          if (inExamples) {
            return alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.14 : 0.08);
          }
          if (selected) {
            return alpha(PGM_FRAME_SELECTION_COLOR, theme.palette.mode === 'dark' ? 0.18 : 0.08);
          }
          return theme.palette.background.paper;
        },
        opacity: committed ? 0.75 : 1,
        cursor: committed ? 'default' : 'pointer',
        minWidth: 0,
      }}
    >
      <Stack direction="row" alignItems="flex-start" sx={{ position: 'relative' }}>
        <Checkbox
          size="small"
          checked={selected}
          disabled={committed}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleFrame(frame.tsec)}
          sx={{
            p: 0.25,
            ml: 0.25,
            color: PGM_FRAME_SELECTION_COLOR,
            '&.Mui-checked': { color: PGM_FRAME_SELECTION_COLOR },
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="img"
            src={imageSrc}
            alt={frame.stamp}
            loading="lazy"
            draggable={false}
            onDoubleClick={(e) => {
              e.stopPropagation();
              window.open(imageSrc, '_blank', 'noopener,noreferrer');
            }}
            sx={{
              width: '100%',
              height: FRAME_THUMB_HEIGHT,
              objectFit: 'cover',
              display: 'block',
              bgcolor: 'grey.900',
            }}
          />
        </Box>
      </Stack>

      <Stack spacing={0.5} sx={{ p: 0.75 }} onClick={(e) => e.stopPropagation()}>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
            {frame.stamp}
          </Typography>
          {committed ? <Chip size="small" label="PGM" color="success" sx={{ height: 18 }} /> : null}
        </Stack>

        {availableCues.map((cue) => {
          const passes = framePassesForCue(frame, cue, runCue);
          const exampleKey = `${normalizedTsec}:${cue}`;
          const isIncorrect = incorrectExampleKeys.has(exampleKey);
          const rejectReason = frameRejectReasonForCue(frame, cue, runCue);
          const targetPool = incorrectExampleTargetPool(passes);

          return (
            <Stack key={cue} direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Tooltip title={rejectReason ?? (passes ? 'PGM pass' : 'PGM fail')}>
                <Chip
                  size="small"
                  label={`${CUE_CHIP_LABEL[cue]} ${passes ? 'pass' : 'fail'}`}
                  color={passes ? 'success' : 'error'}
                  variant="filled"
                  sx={{ height: 20, fontSize: 10 }}
                />
              </Tooltip>
              {isIncorrect ? (
                <Chip
                  size="small"
                  label="Example"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 10 }}
                />
              ) : null}
              {!committed && isIncorrect ? (
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  disabled={removingKey === exampleKey}
                  onClick={() => onRemoveIncorrectExample(frame.tsec, cue, targetPool)}
                  sx={{ minWidth: 0, py: 0, px: 0.5, fontSize: 10, textTransform: 'none' }}
                >
                  {removingKey === exampleKey ? '…' : 'Remove'}
                </Button>
              ) : null}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
});

function PgmIncorrectExamplesSection({
  examples,
  onRemove,
  removingKey,
  onCommit,
  committing,
  commitResults,
}: {
  examples: Array<{
    frame: PgmAuditFrame;
    cue: PgmCueKind;
    pgmPass: boolean;
    pool: PgmFixturePool;
    committed: boolean;
  }>;
  onRemove: (tsec: number, cue: PgmCueKind, pool: PgmFixturePool) => void;
  removingKey: string | null;
  onCommit: () => void;
  committing: boolean;
  commitResults: CommitPgmFixtureFrameResult[] | null;
}) {
  const uncommittedCount = examples.filter((e) => !e.committed).length;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        flexShrink: 0,
        bgcolor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">
          Examples — incorrectly marked ({examples.length})
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="error"
          disabled={uncommittedCount === 0 || committing}
          onClick={onCommit}
        >
          {committing ? 'Committing…' : `Commit examples (${uncommittedCount})`}
        </Button>
      </Stack>

      {examples.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Select frames in the gallery below, pick an example cue, then click{' '}
          <strong>Add selected to examples</strong>.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
          {examples.map(({ frame, cue, pgmPass, pool, committed }) => {
            const imageSrc = auditFrameSrc(frame.imageUrl);
            const removeKey = `${frame.tsec}:${cue}`;
            return (
              <Box
                key={removeKey}
                sx={{
                  flex: '0 0 130px',
                  border: 2,
                  borderColor: committed ? 'success.main' : 'error.main',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  component="img"
                  src={imageSrc}
                  alt={frame.stamp}
                  sx={{ width: '100%', height: 72, objectFit: 'cover', display: 'block', bgcolor: 'grey.900' }}
                />
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5, py: 0.25 }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10, display: 'block' }}>
                      {frame.stamp}
                    </Typography>
                    <Stack direction="row" spacing={0.25} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                      <Chip size="small" label={CUE_CHIP_LABEL[cue]} sx={{ height: 16, fontSize: 9 }} />
                      <Chip
                        size="small"
                        label={pgmPass ? 'PGM pass' : 'PGM fail'}
                        color={pgmPass ? 'success' : 'error'}
                        sx={{ height: 16, fontSize: 9 }}
                      />
                      <Chip
                        size="small"
                        label={pool === 'negative' ? '→ neg test' : '→ pos test'}
                        variant="outlined"
                        sx={{ height: 16, fontSize: 9 }}
                      />
                    </Stack>
                  </Box>
                  {!committed ? (
                    <IconButton
                      size="small"
                      aria-label="Remove example"
                      disabled={removingKey === removeKey}
                      onClick={() => onRemove(frame.tsec, cue, pool)}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  ) : (
                    <Chip size="small" label="PGM" color="success" sx={{ height: 18, fontSize: 9 }} />
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}

      {commitResults && commitResults.length > 0 ? (
        <Alert
          severity={commitResults.every((r) => r.ok) ? 'success' : 'warning'}
          sx={{ mt: 1, py: 0 }}
        >
          Committed {commitResults.filter((r) => r.ok).length}/{commitResults.length} example(s)
        </Alert>
      ) : null}
    </Paper>
  );
}

function PgmAuditAllFramesGallery({
  frames,
  contentId,
  runCue,
  availableCues,
  selections,
  onDataChanged,
}: {
  frames: PgmAuditFrame[];
  contentId: string;
  runCue: PgmCueKind | 'all';
  availableCues: PgmCueKind[];
  selections: SavedSelection[];
  onDataChanged: () => void | Promise<void>;
}) {
  const [selectedTsecs, setSelectedTsecs] = useState<number[]>([]);
  const [exampleCue, setExampleCue] = useState<PgmCueKind>(availableCues[0] ?? 'curtain');
  const [cueVerdictFilter, setCueVerdictFilter] = useState<PgmCueVerdictFilter>('all');
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [bulkMarkingExamples, setBulkMarkingExamples] = useState(false);

  const committedKeys = useMemo(
    () => new Set(selections.filter((s) => s.committedCaseId).map((s) => s.sourceKey)),
    [selections],
  );

  const incorrectExampleKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const s of selections) {
      if (!isIncorrectExampleSelection(s)) continue;
      keys.add(`${normalizeTsec(s.tsec)}:${s.cue}`);
    }
    return keys;
  }, [selections]);

  useEffect(() => {
    setSelectedTsecs([]);
    setCueVerdictFilter('all');
  }, [contentId]);

  useEffect(() => {
    if (availableCues.length > 0 && !availableCues.includes(exampleCue)) {
      setExampleCue(availableCues[0]!);
    }
  }, [availableCues, exampleCue]);

  const filteredFrames = useMemo(
    () =>
      frames.filter((frame) =>
        frameMatchesCueVerdictFilter(frame, exampleCue, runCue, cueVerdictFilter),
      ),
    [frames, exampleCue, runCue, cueVerdictFilter],
  );

  const incorrectSelectionsForCuePool = (cue: PgmCueKind, pool: PgmFixturePool) =>
    selections.filter(
      (s) => s.pool === pool && s.cue === cue && isIncorrectExampleSelection(s),
    );

  const saveIncorrectExamples = async (
    cue: PgmCueKind,
    pool: PgmFixturePool,
    frameRows: Array<{ tsec: number; pgmPass?: boolean; pgmRejectReason?: string }>,
  ) => {
    await apiService.savePgmFixtureSelections({
      cue,
      pool,
      auditSource: 'pgm_audit',
      contentId,
      frames: frameRows,
    });
    await onDataChanged();
  };

  const removeIncorrectExample = async (tsec: number, cue: PgmCueKind, pool: PgmFixturePool) => {
    const normalized = normalizeTsec(tsec);
    const key = `${normalized}:${cue}`;
    setRemovingKey(key);
    try {
      const merged = incorrectSelectionsForCuePool(cue, pool)
        .filter((s) => normalizeTsec(s.tsec) !== normalized)
        .map((s) => ({ tsec: normalizeTsec(s.tsec), pgmPass: s.pgmPass, pgmRejectReason: s.pgmRejectReason }));
      await saveIncorrectExamples(cue, pool, merged);
    } finally {
      setRemovingKey(null);
    }
  };

  const selectedExampleCandidates = useMemo(
    () =>
      frames.filter((f) => {
        const tsec = normalizeTsec(f.tsec);
        return (
          selectedTsecs.includes(tsec) &&
          !incorrectExampleKeys.has(`${tsec}:${exampleCue}`)
        );
      }),
    [frames, selectedTsecs, incorrectExampleKeys, exampleCue],
  );

  const markSelectedAsIncorrect = async () => {
    if (selectedExampleCandidates.length === 0) return;

    setBulkMarkingExamples(true);
    try {
      const cue = exampleCue;
      const byPool = new Map<PgmFixturePool, PgmAuditFrame[]>();
      for (const frame of selectedExampleCandidates) {
        const pgmPass = framePassesForCue(frame, cue, runCue);
        const pool = incorrectExampleTargetPool(pgmPass);
        const list = byPool.get(pool) ?? [];
        list.push(frame);
        byPool.set(pool, list);
      }

      for (const [pool, poolFrames] of Array.from(byPool.entries())) {
        const merged = new Map(
          incorrectSelectionsForCuePool(cue, pool).map((s) => [
            normalizeTsec(s.tsec),
            { tsec: normalizeTsec(s.tsec), pgmPass: s.pgmPass, pgmRejectReason: s.pgmRejectReason },
          ]),
        );
        for (const frame of poolFrames) {
          const tsec = normalizeTsec(frame.tsec);
          merged.set(tsec, {
            tsec,
            pgmPass: framePassesForCue(frame, cue, runCue),
            pgmRejectReason: INCORRECT_EXAMPLE_REASON,
          });
        }
        await saveIncorrectExamples(cue, pool, Array.from(merged.values()));
      }
      setSelectedTsecs([]);
    } finally {
      setBulkMarkingExamples(false);
    }
  };

  const toggleFrame = (tsec: number) => {
    const normalized = normalizeTsec(tsec);
    const sourceKey = buildPgmSourceKey(contentId, normalized);
    if (committedKeys.has(sourceKey)) return;
    setSelectedTsecs((prev) =>
      prev.includes(normalized) ? prev.filter((t) => t !== normalized) : [...prev, normalized],
    );
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(4);
  const rowHeight = pgmAuditFrameRowHeight(availableCues.length);
  const rowCount = Math.ceil(filteredFrames.length / columnCount);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateColumns = () => {
      const width = el.clientWidth - FRAME_CARD_GAP * 2;
      const next = Math.max(1, Math.floor((width + FRAME_CARD_GAP) / (FRAME_CARD_MIN_WIDTH + FRAME_CARD_GAP)));
      setColumnCount((prev) => (prev === next ? prev : next));
    };

    updateColumns();
    const observer = new ResizeObserver(updateColumns);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 4,
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [cueVerdictFilter, exampleCue, contentId]);

  const selectedTsecSet = useMemo(() => new Set(selectedTsecs), [selectedTsecs]);

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: (theme) => theme.palette.surfaceMuted,
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            bgcolor: (theme) => theme.palette.surfaceMuted,
          }}
        >
          <Typography variant="subtitle2">
            {cueVerdictFilter === 'all'
              ? `All frames (${frames.length.toLocaleString()})`
              : `${PGM_CUE_VERDICT_FILTER_LABEL[cueVerdictFilter]} · ${CUE_CHIP_LABEL[exampleCue]} (${filteredFrames.length.toLocaleString()} of ${frames.length.toLocaleString()})`}
            {selectedTsecs.length > 0 ? ` · ${selectedTsecs.length} selected` : ''}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Example cue</InputLabel>
              <Select
                label="Example cue"
                value={exampleCue}
                onChange={(e) => setExampleCue(e.target.value as PgmCueKind)}
              >
                {availableCues.map((cue) => (
                  <MenuItem key={cue} value={cue}>
                    {CUE_CHIP_LABEL[cue]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 128 }}>
              <InputLabel>Show frames</InputLabel>
              <Select
                label="Show frames"
                value={cueVerdictFilter}
                onChange={(e) => setCueVerdictFilter(e.target.value as PgmCueVerdictFilter)}
              >
                {(Object.keys(PGM_CUE_VERDICT_FILTER_LABEL) as PgmCueVerdictFilter[]).map((value) => (
                  <MenuItem key={value} value={value}>
                    {PGM_CUE_VERDICT_FILTER_LABEL[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              color="warning"
              disabled={selectedTsecs.length === 0 || bulkMarkingExamples}
              onClick={() => markSelectedAsIncorrect()}
            >
              {bulkMarkingExamples
                ? 'Adding…'
                : `Add ${selectedExampleCandidates.length || selectedTsecs.length} selected to examples`}
            </Button>
          </Stack>
        </Box>
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            bgcolor: (theme) => theme.palette.surfaceMuted,
            px: 1,
            py: 1,
          }}
        >
          {filteredFrames.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No frames match {CUE_CHIP_LABEL[exampleCue]} {PGM_CUE_VERDICT_FILTER_LABEL[cueVerdictFilter].toLowerCase()}.
            </Typography>
          ) : (
          <Box
            sx={{
              height: rowVirtualizer.getTotalSize(),
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => (
              <Box
                key={virtualRow.key}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                  gap: `${FRAME_CARD_GAP}px`,
                  pb: `${FRAME_CARD_GAP}px`,
                }}
              >
                {Array.from({ length: columnCount }, (_, columnIndex) => {
                  const frameIndex = virtualRow.index * columnCount + columnIndex;
                  const frame = filteredFrames[frameIndex];
                  if (!frame) {
                    return <Box key={`empty-${virtualRow.index}-${columnIndex}`} />;
                  }

                  const normalizedTsec = normalizeTsec(frame.tsec);
                  const sourceKey = buildPgmSourceKey(contentId, normalizedTsec);
                  const committed = committedKeys.has(sourceKey);
                  const selected = selectedTsecSet.has(normalizedTsec);
                  const inExamples = availableCues.some((cue) =>
                    incorrectExampleKeys.has(`${normalizedTsec}:${cue}`),
                  );

                  return (
                    <PgmAuditFrameCard
                      key={normalizedTsec}
                      frame={frame}
                      contentId={contentId}
                      runCue={runCue}
                      availableCues={availableCues}
                      committed={committed}
                      selected={selected}
                      inExamples={inExamples}
                      incorrectExampleKeys={incorrectExampleKeys}
                      removingKey={removingKey}
                      onToggleFrame={toggleFrame}
                      onRemoveIncorrectExample={removeIncorrectExample}
                    />
                  );
                })}
              </Box>
            ))}
          </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

export default function PgmAuditDetailPage() {
  const { contentId = '' } = useParams<{ contentId: string }>();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['pgm-audit-detail', contentId],
    queryFn: () => apiService.getPgmAuditDetail(contentId),
    enabled: Boolean(contentId),
  });

  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [exampleCommitResults, setExampleCommitResults] = useState<CommitPgmFixtureFrameResult[] | null>(null);

  const auditAnchor = detailQuery.data?.auditAnchor;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['pgm-audit-detail', contentId] });
  };

  const meta = detailQuery.data?.meta;
  const runCue = meta?.cue ?? 'all';
  const availableCues = cuesAvailableForRun(runCue);

  const selections: SavedSelection[] = useMemo(
    () =>
      (detailQuery.data?.selections ?? []).map((s) => ({
        tsec: s.tsec,
        pool: s.pool ?? 'positive',
        cue: s.cue,
        committedCaseId: s.committedCaseId,
        sourceKey: s.sourceKey,
        pgmPass: s.pgmPass,
        pgmRejectReason: s.pgmRejectReason,
        mentionId: s.mentionId,
        auditSource: s.auditSource,
      })),
    [detailQuery.data?.selections],
  );

  const incorrectExamples = useMemo(() => {
    const frameList = detailQuery.data?.frames ?? [];
    return selections
      .filter((s) => isIncorrectExampleSelection(s))
      .map((s) => {
        const normalized = normalizeTsec(s.tsec);
        return {
          frame: frameList.find((f) => normalizeTsec(f.tsec) === normalized),
          cue: s.cue,
          pgmPass: s.pgmPass ?? false,
          pool: s.pool,
          committed: Boolean(s.committedCaseId),
        };
      })
      .filter(
        (x): x is {
          frame: PgmAuditFrame;
          cue: PgmCueKind;
          pgmPass: boolean;
          pool: PgmFixturePool;
          committed: boolean;
        } => Boolean(x.frame),
      );
  }, [selections, detailQuery.data?.frames]);

  const removeIncorrectExample = async (tsec: number, cue: PgmCueKind, pool: PgmFixturePool) => {
    const normalized = normalizeTsec(tsec);
    const key = `${normalized}:${cue}`;
    setRemovingKey(key);
    try {
      const merged = selections
        .filter((s) => s.pool === pool && s.cue === cue && isIncorrectExampleSelection(s))
        .filter((s) => normalizeTsec(s.tsec) !== normalized)
        .map((s) => ({ tsec: normalizeTsec(s.tsec), pgmPass: s.pgmPass, pgmRejectReason: s.pgmRejectReason }));
      await apiService.savePgmFixtureSelections({
        cue,
        pool,
        auditSource: 'pgm_audit',
        contentId,
        frames: merged,
      });
      await refresh();
    } finally {
      setRemovingKey(null);
    }
  };

  const commitExamplesMutation = useMutation({
    mutationFn: async () => {
      if (!auditAnchor) throw new Error('Audit anchor not loaded');
      const uncommitted = incorrectExamples.filter((e) => !e.committed);
      const results: CommitPgmFixtureFrameResult[] = [];

      const byCuePool = new Map<string, typeof uncommitted>();
      for (const ex of uncommitted) {
        const groupKey = `${ex.cue}:${ex.pool}`;
        const list = byCuePool.get(groupKey) ?? [];
        list.push(ex);
        byCuePool.set(groupKey, list);
      }

      for (const [, examples] of Array.from(byCuePool.entries())) {
        const cue = examples[0]!.cue;
        const pool = examples[0]!.pool;
        const data = await apiService.commitPgmFixtures({
          cue,
          pool,
          frames: examples.map((ex) => ({
            learningExampleId: auditAnchor.learningExampleId,
            mentionId: auditAnchor.mentionId,
            contentId,
            tsec: ex.frame.tsec,
          })),
        });
        results.push(...data.results);
      }
      return results;
    },
    onSuccess: async (results) => {
      setExampleCommitResults(results);
      await refresh();
    },
  });

  const passSummary =
    runCue === 'all'
      ? PGM_CUE_KINDS.map(
          (cue) =>
            `${CUE_CHIP_LABEL[cue]} ${meta?.passCountByCue?.[cue] ?? 0}/${meta?.frameCount ?? 0}`,
        ).join(' · ')
      : `${meta?.passCount ?? 0}/${meta?.frameCount ?? 0} pass (${runCue})`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
        <Button
          component={RouterLink}
          to={ROUTES_NAMES.PGM_AUDIT}
          startIcon={<ArrowBackIcon />}
          size="small"
          sx={{ mb: 1 }}
        >
          All audit runs
        </Button>

        {detailQuery.isLoading ? (
          <CircularProgress size={24} />
        ) : detailQuery.data ? (
          <>
            <Typography variant="h5">{detailQuery.data.meta.title ?? contentId}</Typography>
            <Typography variant="body2" color="text.secondary">
              {detailQuery.data.meta.frameCount.toLocaleString()} frames · {passSummary} ·{' '}
              {new Date(detailQuery.data.meta.sampledAt).toLocaleString('es-CL')}
            </Typography>
          </>
        ) : (
          <Typography color="text.secondary">Audit run not found.</Typography>
        )}
      </Box>

      {detailQuery.data ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            px: 3,
            pb: 2,
            gap: 1.5,
            overflow: 'hidden',
          }}
        >
          <PgmIncorrectExamplesSection
            examples={incorrectExamples}
            onRemove={removeIncorrectExample}
            removingKey={removingKey}
            onCommit={() => commitExamplesMutation.mutate()}
            committing={commitExamplesMutation.isPending}
            commitResults={exampleCommitResults}
          />

          <PgmAuditAllFramesGallery
            frames={detailQuery.data.frames}
            contentId={contentId}
            runCue={runCue}
            availableCues={availableCues}
            selections={selections}
            onDataChanged={refresh}
          />
        </Box>
      ) : null}
    </Box>
  );
}
